import React, { useState } from "react";

import { keyframes, styled } from "@mui/material";
import { getFontWeightAndStyle } from "@src/formatters";

import { getMediaQuery } from "../../getMediaQuery";
import { cls } from "../../util";
import { useMessageBoxContext } from "../context/MessageBoxContext";

import { CloseButton } from "./CloseButton";

export interface MessageBoxProps {
  className?: string;
}

export const MessageBox = ({ className }: MessageBoxProps) => {
  const { isFe, widget, visible, onClose } = useMessageBoxContext();
  const [isVisible, setIsVisible] = useState<boolean>(visible ?? true);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  const title = widget.variables?.textReplacements?.title;
  const body = widget.variables?.textReplacements?.body;

  return (
    <Container
      isVisible={isVisible}
      className={cls("ig-message-box-container", className)}
      style={{ boxSizing: "border-box" }}
    >
      {!isFe && <CloseButton onClick={handleClose} />}
      <Title className={"ig-message-box-title"}>{title ?? ""}</Title>
      <Body className={"ig-message-box-body"}>{body ?? ""}</Body>
    </Container>
  );
};

interface ContainerProps {
  isVisible: boolean;
}

const Container = styled("div")(({ isVisible }: ContainerProps) => {
  const { isFe, widget, hasMediaQuery } = useMessageBoxContext();
  const { container: styles } = widget.styles;
  const display = isFe ? "block" : isVisible ? "flex" : "none";
  const position = isFe ? "unset" : "absolute";

  const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;
  const slideInFromLeft = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

  return {
    ...(hasMediaQuery && isVisible ? getMediaQuery(widget) : undefined),
    fontSmooth: "antialiased",
    display,
    zIndex: 998,
    background: styles.backgroundColor,
    color: styles.textColor,
    borderRadius: "0px 0px 0px 0px",
    top: isFe ? "unset" : "20%",
    ...(widget.styles.placement === "top-right" && {
      right: isFe
        ? "unset"
        : widget.variant === "messageBoxSlideOutWidget1"
          ? 0
          : "5%",
    }),
    ...(widget.styles.placement === "top-left" && {
      left: isFe
        ? "unset"
        : widget.variant === "messageBoxSlideOutWidget1"
          ? 0
          : "5%",
    }),
    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
    position,
    justifyContent: "start",
    flexDirection: "column",
    width: 350,
    padding: "20px",
    minHeight: 90,
    ...(!isFe && {
      animation: isVisible
        ? `${widget.styles.placement === "top-left" ? slideInFromLeft : slideInFromRight} 0.5s ease-out forwards`
        : "none",
      transition: "transform 1s ease",
    }),
  };
});

const Title = styled("div")(() => {
  const { widget } = useMessageBoxContext();
  const { fontWeight, fontStyle } = getFontWeightAndStyle(
    widget.styles.typography.title.fontStyle
  );
  return {
    fontSize: widget.styles.typography.title.fontSize,
    lineHeight: "24px",
    fontWeight,
    fontStyle,
    textAlign: "start",
  };
});

const Body = styled("div")(() => {
  const { widget } = useMessageBoxContext();
  const { fontWeight, fontStyle } = getFontWeightAndStyle(
    widget.styles.typography.title.fontStyle
  );
  return {
    fontSize: widget.styles.typography.description.fontSize,
    lineHeight: "21px",
    fontWeight,
    fontStyle,
    textAlign: "start",
    opacity: 0.8,
  };
});
